import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '@/context/auth-context';
import { useNavigate } from 'react-router-dom';
import { useSite } from '@/contexts/SiteContext';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../../components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Progress } from '../../components/ui/progress';
import { Alert, AlertDescription, AlertTitle } from '../../components/ui/alert';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Area,
  AreaChart,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  ScatterChart,
  Scatter,
  Cell,
} from 'recharts';
import {
  TrendingUp,
  TrendingDown,
  DollarSign,
  Users,
  ShoppingCart,
  Target,
  Calendar,
  Download,
  Filter,
  BarChart3,
  PieChart,
  Activity,
  Zap,
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { authFetch } from '@/utils/auth-fetch';
import { pivotDailySeries, toDailyMetricKey, type DailySeriesPoint, type DailySeriesResponse } from './segmentTimeSeries';
import { isRecorded, generateInsights, recordedRevenueTotal, revenueShare, type SegmentPerformance, type Measured, type MetricKey } from './segmentInsights';

/** The one way an unrecorded value is shown. */
const NotRecorded = ({ what }: { what?: string }) => (
  <span className="text-gray-400 italic" data-testid="not-recorded">{what ? `${what} not recorded` : 'not recorded'}</span>
);

interface DimensionComparison {
  dimension: string;
  segments: {
    name: string;
    value: number;
  }[];
}

const COLORS = ['#8B5CF6', '#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#EC4899', '#14B8A6'];

export default function SegmentAnalytics() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { selectedSite } = useSite();
  const [segmentPerformance, setSegmentPerformance] = useState<SegmentPerformance[]>([]);
  const [selectedMetric, setSelectedMetric] = useState<string>('conversionRate');
  const [selectedPeriod, setSelectedPeriod] = useState<string>('90d'); // Default to last 90 days
  const [loading, setLoading] = useState(false);
  // Set when the performance request fails. The metrics are then UNAVAILABLE, never substituted:
  // until #6306 this page fell back to inventing bounce rate, session duration, repeat purchases
  // and every trend with Math.random() (no-silent-fallbacks rule; codex-reviewer-2 on #6306).
  const [performanceError, setPerformanceError] = useState<string | null>(null);
  // How many active segments have at least one recorded segment_analytics_daily row in the window
  // (from the /performance envelope). null = not loaded. 0 = nothing was measured: the page then
  // shows ONE page-level state instead of the endpoint's COALESCEd zeros as if they were metrics
  // (task-7d6o29fdh7 b; production 2026-09-17 had 0 daily rows for 14 segments).
  const [measuredSegments, setMeasuredSegments] = useState<number | null>(null);
  const [performanceWindow, setPerformanceWindow] = useState<{ startDate: string; endDate: string } | null>(null);
  // Recorded daily rows (segment_analytics_daily) behind the Trends tab. null = not loaded yet;
  // [] = the source holds no rows for this site/period, rendered as such. Until task-s830jr3i34 the
  // two Trends charts were drawn from Math.random() jitter around the latest aggregate.
  const [dailySeries, setDailySeries] = useState<DailySeriesPoint[] | null>(null);
  const [dailySeriesError, setDailySeriesError] = useState<string | null>(null);
  // Every (site, period) selection gets a generation number. A response is applied only if it
  // belongs to the CURRENT generation, so a slow request for a previous site or period can never
  // overwrite the current selection's data (codex on #6313: shared segment slugs would make another
  // site's series look valid). Both fetches use it; the race was the same for both.
  const requestGeneration = useRef(0);
  const isCurrent = (generation: number) => generation === requestGeneration.current;

  // Fetch performance + recorded daily rows when the site or period changes
  useEffect(() => {
    if (selectedSite) {
      const generation = ++requestGeneration.current;
      // Clear what belonged to the previous selection so it is not shown while the new one loads.
      setDailySeries(null);
      setDailySeriesError(null);
      fetchSegmentPerformance(generation);
      fetchDailySeries(generation);
    }
  }, [selectedSite, selectedPeriod]);

  const fetchDailySeries = async (generation: number) => {
    if (!selectedSite) return;
    const siteId = selectedSite._id || selectedSite.id;
    try {
      const response = await authFetch(`/analytics/segments/${siteId}/timeseries?period=${selectedPeriod}`);
      if (!isCurrent(generation)) return; // a newer selection owns the screen now
      if (!response.ok) {
        const message = `Segment daily series request failed: HTTP ${response.status} ${response.statusText}`.trim();
        console.error('Timeseries endpoint failed:', response.status, response.statusText);
        setDailySeries(null);
        setDailySeriesError(message);
        return;
      }
      const data: unknown = await response.json();
      if (!isCurrent(generation)) return;
      // A 200 whose body is not {series: [...]} is a CONTRACT failure, not "no data recorded".
      // Only a real empty array earns the empty state.
      const series = (data as DailySeriesResponse | null)?.series;
      if (!Array.isArray(series)) {
        const shape = data === null ? 'null' : Array.isArray(data) ? 'array' : typeof data === 'object' ? `object with keys [${Object.keys(data as object).join(', ')}]` : typeof data;
        const message = `Segment daily series response malformed: expected {series: [...]}, got ${shape}`;
        console.error(message, data);
        setDailySeries(null);
        setDailySeriesError(message);
        return;
      }
      setDailySeries(series);
      setDailySeriesError(null);
    } catch (error) {
      if (!isCurrent(generation)) return;
      const message = `Segment daily series request failed: ${error instanceof Error ? error.message : String(error)}`;
      console.error('Error fetching daily series:', error);
      setDailySeries(null);
      setDailySeriesError(message);
    }
  };

  const fetchSegmentPerformance = async (generation: number) => {
    if (!selectedSite) return;
    
    setLoading(true);
    try {
      const siteId = selectedSite._id || selectedSite.id;
      const response = await authFetch(
        `/analytics/segments/${siteId}/performance?period=${selectedPeriod}`
      );
      if (!isCurrent(generation)) return; // superseded by a newer selection
      
      if (!response.ok) {
        // Surface the failure. Do NOT fall back to the distribution endpoint and invent the rest.
        const message = `Segment performance request failed: HTTP ${response.status} ${response.statusText}`.trim();
        console.error('Performance endpoint failed:', response.status, response.statusText);
        setSegmentPerformance([]);
        setPerformanceError(message);
        toast.error(message);
        return;
      }
      
      const data = await response.json();
      if (!isCurrent(generation)) return;

      // The envelope must say what was measured; a 200 without it is a contract failure, not data.
      if (!data || !Array.isArray(data.performance) || typeof data.measuredSegments !== 'number') {
        const shape = data === null ? 'null' : typeof data === 'object' ? `object with keys [${Object.keys(data).join(', ')}]` : typeof data;
        const message = `Segment performance response malformed: expected {performance: [...], measuredSegments: n}, got ${shape}`;
        console.error(message, data);
        setSegmentPerformance([]);
        setMeasuredSegments(null);
        setPerformanceError(message);
        toast.error(message);
        return;
      }
      
      // userCount mirrors metrics.totalUsers; null when not recorded (never `|| 0`).
      const performanceData = data.performance.map(item => ({
        ...item,
        userCount: isRecorded(item.metrics?.totalUsers) ? item.metrics.totalUsers : (isRecorded(item.userCount) ? item.userCount : null)
      }));
      
      setSegmentPerformance(performanceData);
      setMeasuredSegments(data.measuredSegments);
      setPerformanceWindow(data.window && typeof data.window.startDate === 'string' && typeof data.window.endDate === 'string' ? data.window : null);
      setPerformanceError(null);
    } catch (error) {
      if (!isCurrent(generation)) return;
      const message = `Segment performance request failed: ${error instanceof Error ? error.message : String(error)}`;
      console.error('Error fetching performance:', error);
      setSegmentPerformance([]);
      setPerformanceError(message);
      toast.error(message);
    } finally {
      // Only the current generation may end the loading state; a stale one would flash the
      // previous selection's page while the current request is still in flight.
      if (isCurrent(generation)) setLoading(false);
    }
  };

  const getMetricInfo = (metric: string) => {
    const metrics = {
      conversionRate: { label: 'Conversion Rate', format: (v: number) => `${v.toFixed(1)}%`, icon: Target },
      avgOrderValue: { label: 'Avg Order Value', format: (v: number) => `$${v.toFixed(0)}`, icon: DollarSign },
      totalRevenue: { label: 'Total Revenue', format: (v: number) => `$${v.toLocaleString('en-US', { maximumFractionDigits: 0 })}`, icon: DollarSign },
      avgSessionDuration: { label: 'Avg Session', format: (v: number) => `${Math.round(v)}s`, icon: Activity },
      bounceRate: { label: 'Bounce Rate', format: (v: number) => `${v.toFixed(1)}%`, icon: TrendingDown },
      repeatPurchaseRate: { label: 'Repeat Purchase', format: (v: number) => `${v.toFixed(1)}%`, icon: ShoppingCart },
      lifetimeValue: { label: 'Lifetime Value', format: (v: number) => `$${v.toFixed(0)}`, icon: Users },
    };
    return metrics[metric as keyof typeof metrics] || { label: metric, format: (v: number) => v.toFixed(1), icon: BarChart3 };
  };

  // Recorded values first, descending; unrecorded (null) last, in a stable order.
  const metricKey = selectedMetric as MetricKey;
  const topPerformers = [...segmentPerformance]
    .sort((a, b) => {
      const av = a.metrics[metricKey], bv = b.metrics[metricKey];
      if (isRecorded(av) && isRecorded(bv)) return bv - av;
      if (isRecorded(av)) return -1;
      if (isRecorded(bv)) return 1;
      return a.segmentName.localeCompare(b.segmentName);
    })
    .slice(0, 5);

  /** Format a metric for display, or say it was not recorded. */
  const renderMetric = (info: { format: (v: number) => string }, value: Measured) =>
    isRecorded(value) ? info.format(value) : <NotRecorded />;

  // Recorded daily rows pivoted for the top three segments. Empty when nothing was recorded.
  const topSlugs = topPerformers.slice(0, 3).map(s => s.segmentSlug);
  const dailyMetricKey = toDailyMetricKey(selectedMetric);
  const trendChartData = dailySeries && dailyMetricKey ? pivotDailySeries(dailySeries, topSlugs, dailyMetricKey) : [];
  const growthChartData = dailySeries ? pivotDailySeries(dailySeries, topSlugs, 'totalUsers') : [];

  const dimensionComparison: DimensionComparison[] = ['behavioral', 'value', 'lifecycle', 'engagement'].map(dim => ({
    dimension: dim,
    segments: segmentPerformance
      .filter(s => s.dimension === dim && isRecorded(s.metrics[metricKey])) // unrecorded segments are not bars of height 0
      .slice(0, 3)
      .map(s => ({
        name: s.segmentName,
        value: s.metrics[metricKey] as number
      }))
  }));

  const getTrendIcon = (trend: number) => {
    if (trend > 0) return <TrendingUp className="w-4 h-4 text-green-600" />;
    if (trend < 0) return <TrendingDown className="w-4 h-4 text-red-600" />;
    return <span className="w-4 h-4 text-gray-400">-</span>;
  };

  // Export is only meaningful when something was measured: with measuredSegments 0 the rows are the
  // endpoint's COALESCEd zeros, and a CSV of them beside a "nothing recorded" card would be the same
  // fabrication in a file (both codex legs on #6314).
  const canExport = !loading && !performanceError && measuredSegments !== null && measuredSegments > 0 && segmentPerformance.length > 0;

  const exportData = () => {
    if (!canExport) return;
    const csv = [
      ['Segment', 'Dimension', 'Users', 'Conversion Rate', 'AOV', 'Revenue', 'LTV'],
      // An unrecorded value is an EMPTY cell, never 0.
      ...segmentPerformance.map(s => [
        s.segmentName,
        s.dimension,
        isRecorded(s.userCount) ? s.userCount : '',
        isRecorded(s.metrics.conversionRate) ? s.metrics.conversionRate : '',
        isRecorded(s.metrics.avgOrderValue) ? s.metrics.avgOrderValue : '',
        isRecorded(s.metrics.totalRevenue) ? s.metrics.totalRevenue : '',
        isRecorded(s.metrics.lifetimeValue) ? s.metrics.lifetimeValue : ''
      ])
    ].map(row => row.join(',')).join('\n');
    
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const siteId = selectedSite._id || selectedSite.id;
    a.download = `segment-analytics-${siteId}-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  if (!selectedSite) {
    return (
      <div className="container mx-auto p-6">
        <div className="mb-6">
          <h1 className="text-3xl font-bold mb-2">Segment Performance Analytics</h1>
          <p className="text-gray-600">
            Please select a site from the dropdown above to view segment analytics
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold mb-2">Segment Performance Analytics</h1>
        <p className="text-gray-600">
          Deep dive into segment performance metrics and comparisons
        </p>
      </div>

      {/* Controls */}
      <div className="flex flex-wrap gap-4 mb-6">
        <Select value={selectedPeriod || '30d'} onValueChange={setSelectedPeriod}>
          <SelectTrigger className="w-[150px]">
            <SelectValue placeholder="Time period" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="7d">Last 7 days</SelectItem>
            <SelectItem value="30d">Last 30 days</SelectItem>
            <SelectItem value="90d">Last 90 days</SelectItem>
            <SelectItem value="365d">Last year</SelectItem>
          </SelectContent>
        </Select>

        <Select value={selectedMetric || 'conversionRate'} onValueChange={setSelectedMetric}>
          <SelectTrigger className="w-[200px]">
            <SelectValue placeholder="Select metric" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="conversionRate">Conversion Rate</SelectItem>
            <SelectItem value="avgOrderValue">Avg Order Value</SelectItem>
            <SelectItem value="totalRevenue">Total Revenue</SelectItem>
            <SelectItem value="lifetimeValue">Lifetime Value</SelectItem>
            <SelectItem value="repeatPurchaseRate">Repeat Purchase</SelectItem>
            <SelectItem value="avgSessionDuration">Session Duration</SelectItem>
            <SelectItem value="bounceRate">Bounce Rate</SelectItem>
          </SelectContent>
        </Select>

        <Button
          variant="outline"
          onClick={exportData}
          className="ml-auto"
          disabled={!canExport}
          title={canExport ? undefined : 'Nothing measured to export'}
          data-testid="segment-export-csv"
        >
          <Download className="w-4 h-4 mr-2" />
          Export CSV
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
        </div>
      ) : performanceError ? (
        <Alert variant="destructive" data-testid="segment-performance-unavailable">
          <AlertTitle>Segment performance metrics unavailable</AlertTitle>
          <AlertDescription>
            {performanceError}. No metrics are shown because none were measured for this request.
          </AlertDescription>
        </Alert>
      ) : measuredSegments === 0 ? (
        <Card data-testid="segment-performance-empty">
          <CardHeader>
            <CardTitle>No daily segment metrics recorded</CardTitle>
            <CardDescription>
              {segmentPerformance.length} active segment{segmentPerformance.length === 1 ? '' : 's'} exist for this site, but segment_analytics_daily holds no rows for any of them
              {performanceWindow ? ` between ${performanceWindow.startDate} and ${performanceWindow.endDate}` : ' in the selected period'}.
              Conversion, revenue, session and trend figures are shown only once the segment analytics collector has recorded them; they are never estimated. Try a longer period, or check the collector.
            </CardDescription>
          </CardHeader>
        </Card>
      ) : (
        <Tabs defaultValue="overview" className="w-full">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="comparison">Comparison</TabsTrigger>
            <TabsTrigger value="trends">Trends</TabsTrigger>
            <TabsTrigger value="insights">Insights</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            {/* Top Performers */}
            <Card>
              <CardHeader>
                <CardTitle>Top Performing Segments</CardTitle>
                <CardDescription>
                  By {getMetricInfo(selectedMetric).label}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {topPerformers.map((segment, idx) => {
                    const metricInfo = getMetricInfo(selectedMetric);
                    const value = segment.metrics[selectedMetric as keyof typeof segment.metrics];
                    const trend = segment.trends[selectedMetric === 'conversionRate' ? 'conversionTrend' : 
                                               selectedMetric === 'totalRevenue' ? 'revenueTrend' : 'userGrowth'];
                    
                    return (
                      <div key={segment.segmentSlug} className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-bold`}
                               style={{ backgroundColor: COLORS[idx] }}>
                            {idx + 1}
                          </div>
                          <div>
                            <div className="font-medium">{segment.segmentName}</div>
                            <div className="text-sm text-gray-500">
                              {isRecorded(segment.userCount) ? `${segment.userCount.toLocaleString()} users` : <NotRecorded what="users" />} • {segment.dimension}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="text-right">
                            <div className="font-semibold text-lg">
                              {renderMetric(metricInfo, value)}
                            </div>
                            <div className="flex items-center gap-1 text-sm">
                              {isRecorded(trend) ? (
                                <>
                                  {getTrendIcon(trend)}
                                  <span className={trend > 0 ? 'text-green-600' : trend < 0 ? 'text-red-600' : 'text-gray-400'}>
                                    {Math.abs(trend).toFixed(1)}%
                                  </span>
                                </>
                              ) : (
                                <NotRecorded what="trend" />
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            {/* Dimension Performance */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle>Performance by Dimension</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={dimensionComparison}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="dimension" />
                        <YAxis />
                        <Tooltip />
                        <Bar dataKey="segments[0].value" name="Top Segment" fill={COLORS[0]} />
                        <Bar dataKey="segments[1].value" name="2nd Segment" fill={COLORS[1]} />
                        <Bar dataKey="segments[2].value" name="3rd Segment" fill={COLORS[2]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Segment Distribution</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <ScatterChart>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="userCount" name="Users" />
                        <YAxis dataKey="conversionRate" name="Conversion %" />
                        <Tooltip cursor={{ strokeDasharray: '3 3' }} />
                        <Scatter
                          name="Segments"
                          data={segmentPerformance.map(s => ({
                            userCount: s.userCount,
                            conversionRate: s.metrics.conversionRate,
                            revenue: s.metrics.totalRevenue,
                            name: s.segmentName
                          }))}
                          fill="#8884d8"
                        >
                          {segmentPerformance.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Scatter>
                      </ScatterChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Comparison Tab */}
          <TabsContent value="comparison" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Multi-Metric Comparison</CardTitle>
                <CardDescription>
                  Compare segments across multiple performance metrics
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-96">
                  <ResponsiveContainer width="100%" height="100%">
                    <RadarChart data={getRadarData(segmentPerformance.slice(0, 5))}>
                      <PolarGrid />
                      <PolarAngleAxis dataKey="metric" />
                      <PolarRadiusAxis angle={90} domain={[0, 100]} />
                      {segmentPerformance.slice(0, 5).map((segment, idx) => (
                        <Radar
                          key={segment.segmentSlug}
                          name={segment.segmentName}
                          dataKey={segment.segmentSlug}
                          stroke={COLORS[idx]}
                          fill={COLORS[idx]}
                          fillOpacity={0.3}
                        />
                      ))}
                      <Legend />
                    </RadarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Metric Cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {['conversionRate', 'avgOrderValue', 'totalRevenue', 'lifetimeValue'].map((key) => {
                const topSegment = topPerformers[0];
                if (!topSegment) return null;
                
                const info = getMetricInfo(key);
                const value = topSegment.metrics[key as keyof typeof topSegment.metrics];
                const Icon = info.icon;
                
                return (
                  <Card key={key}>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium flex items-center gap-2">
                        <Icon className="w-4 h-4" />
                        {info.label}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold">{renderMetric(info, value)}</div>
                      <p className="text-xs text-muted-foreground mt-1">
                        {topSegment.segmentName}
                      </p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </TabsContent>

          {/* Trends Tab */}
          <TabsContent value="trends" className="space-y-6">
            {dailySeriesError ? (
              <Alert variant="destructive" data-testid="segment-timeseries-unavailable">
                <AlertTitle>Daily segment metrics unavailable</AlertTitle>
                <AlertDescription>
                  {dailySeriesError}. The Performance Trends and User Growth charts are drawn only from recorded daily rows, so nothing is shown.
                </AlertDescription>
              </Alert>
            ) : dailySeries === null ? (
              <div className="flex items-center justify-center h-64" data-testid="segment-timeseries-loading">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
              </div>
            ) : dailySeries.length === 0 ? (
              <Card data-testid="segment-timeseries-empty">
                <CardHeader>
                  <CardTitle>No daily segment metrics recorded</CardTitle>
                  <CardDescription>
                    segment_analytics_daily holds no rows for this site in the selected period. The Performance Trends and User Growth charts appear once the segment analytics collector has recorded daily metrics; they are never estimated.
                  </CardDescription>
                </CardHeader>
              </Card>
            ) : (
            <>
            <Card>
              <CardHeader>
                <CardTitle>Performance Trends</CardTitle>
                <CardDescription>
                  {getMetricInfo(selectedMetric).label} over time, from recorded daily rows
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-96">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={trendChartData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      {topPerformers.slice(0, 3).map((segment, idx) => (
                        <Line
                          key={segment.segmentSlug}
                          type="monotone"
                          dataKey={segment.segmentSlug}
                          name={segment.segmentName}
                          stroke={COLORS[idx]}
                          strokeWidth={2}
                        />
                      ))}
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Growth Analysis */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle>User Growth by Segment</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={growthChartData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" />
                        <YAxis />
                        <Tooltip />
                        {topPerformers.slice(0, 3).map((segment, idx) => (
                          <Area
                            key={segment.segmentSlug}
                            type="monotone"
                            dataKey={segment.segmentSlug}
                            stackId="1"
                            stroke={COLORS[idx]}
                            fill={COLORS[idx]}
                          />
                        ))}
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Revenue Contribution</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {segmentPerformance.slice(0, 5).map((segment) => {
                      // Two separate facts (codex on #6316): WAS revenue recorded, and IS a share defined.
                      // A recorded $0 is a measurement and is shown as one; its share is undefined only
                      // when no recorded revenue exists in the window, and that is said as such.
                      const revenue = segment.metrics.totalRevenue;
                      const share = revenueShare(segment, segmentPerformance);
                      const shareText = share === null
                        ? (recordedRevenueTotal(segmentPerformance) > 0 ? null : 'share n/a — no recorded revenue in the window')
                        : `${share.toFixed(1)}%`;
                      
                      return (
                        <div key={segment.segmentSlug} data-testid={`revenue-contribution-${segment.segmentSlug}`}>
                          <div className="flex justify-between mb-1">
                            <span className="text-sm font-medium">{segment.segmentName}</span>
                            <span className="text-sm text-gray-500">
                              {!isRecorded(revenue)
                                ? <NotRecorded what="revenue" />
                                : `$${revenue.toLocaleString('en-US', { maximumFractionDigits: 0 })}${shareText ? ` · ${shareText}` : ''}`}
                            </span>
                          </div>
                          {share !== null && <Progress value={share} className="h-2" />}
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            </div>
            </>
            )}
          </TabsContent>

          {/* Insights Tab */}
          <TabsContent value="insights" className="space-y-6">
            <div className="grid gap-4">
              {generateInsights(segmentPerformance, selectedMetric).map((insight, idx) => (
                <Card key={idx}>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Zap className="w-5 h-5 text-yellow-600" />
                      {insight.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-600 mb-3">{insight.description}</p>
                    <div className="flex flex-wrap gap-2">
                      {insight.actions.map((action, i) => (
                        <Badge key={i} variant="outline">
                          {action}
                        </Badge>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
}

// Helper functions

function getRadarData(segments: SegmentPerformance[]) {
  const metrics = [
    { metric: 'Conversion', key: 'conversionRate', max: 20 },
    { metric: 'AOV', key: 'avgOrderValue', max: 300 },
    { metric: 'Session Time', key: 'avgSessionDuration', max: 800 },
    { metric: 'Repeat Rate', key: 'repeatPurchaseRate', max: 100 },
    { metric: 'LTV', key: 'lifetimeValue', max: 3000 }
  ];
  
  return metrics.map(m => {
    const dataPoint: any = { metric: m.metric };
    segments.forEach(segment => {
      const value = segment.metrics[m.key as MetricKey];
      if (isRecorded(value)) dataPoint[segment.segmentSlug] = (value / m.max) * 100; // unrecorded: no point, not 0
    });
    return dataPoint;
  });
}

function getMetricInfo(metric: string): any {
  if (metric === 'all') {
    return {
      conversionRate: { label: 'Conversion Rate', format: (v: number) => `${v.toFixed(1)}%`, icon: Target },
      avgOrderValue: { label: 'Avg Order Value', format: (v: number) => `$${v.toFixed(0)}`, icon: DollarSign },
      repeatPurchaseRate: { label: 'Repeat Purchase', format: (v: number) => `${v.toFixed(1)}%`, icon: ShoppingCart },
      lifetimeValue: { label: 'Lifetime Value', format: (v: number) => `$${v.toFixed(0)}`, icon: Users },
    };
  }
  return {
    label: metric,
    format: (v: number) => v.toFixed(1),
    icon: BarChart3
  };
}